/* Fitness Society theme JS — reveals, header, drawer hooks. Vanilla, no deps. */
(() => {
  'use strict';

  /* Scroll reveals */
  const initReveals = () => {
    const els = document.querySelectorAll('.reveal');
    if (!els.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -5% 0px' });
    els.forEach((el) => io.observe(el));
  };

  /* Header: shadow on scroll, mobile menu toggle */
  const initHeader = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    const onScrollEdge = new IntersectionObserver(([e]) => {
      header.classList.toggle('is-scrolled', !e.isIntersecting);
    });
    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:1px;';
    document.body.prepend(sentinel);
    onScrollEdge.observe(sentinel);

    const toggle = header.querySelector('[data-menu-toggle]');
    const drawer = header.querySelector('[data-menu-drawer]');
    if (toggle && drawer) {
      toggle.addEventListener('click', () => {
        const open = header.classList.toggle('menu-open');
        toggle.setAttribute('aria-expanded', open);
        drawer.toggleAttribute('aria-hidden', !open);
        document.documentElement.classList.toggle('no-scroll', open);
      });
      drawer.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape' && header.classList.contains('menu-open')) toggle.click();
      });
    }
  };

  /* Product: variant selection, quantity, sticky mobile ATC */

  /* Cart: drawer + cart page share contents. Updates via Section Rendering API. */

  /* Predictive search: debounced suggestions via Shopify predictive API */

  /* UGC video carousels: play on click, arrow scroll, pause others */
  const initVideoCarousels = () => {
    document.querySelectorAll('[data-vcar]').forEach((car) => {
      const rail = car.querySelector('[data-vcar-rail]');
      car.querySelectorAll('[data-vcard-play]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const frame = btn.closest('[data-vcard]');
          const video = frame.querySelector('video');
          if (!video) return;
          car.querySelectorAll('video').forEach((v) => { if (v !== video) { v.pause(); v.closest('[data-vcard]')?.classList.remove('is-playing'); } });
          frame.classList.add('is-playing');
          video.play();
        });
      });
      const step = () => (rail ? rail.clientWidth * 0.8 : 300);
      car.querySelector('[data-vcar-next]')?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
      car.querySelector('[data-vcar-prev]')?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
    });
  };

  /* Product tabs + read-more clamp */
  const initTabs = () => {
    const root = document.querySelector('[data-tabs]');
    if (!root) return;
    const tabs = [...root.querySelectorAll('[data-tab]')];
    const panels = [...root.querySelectorAll('[data-panel]')];
    const show = (key) => {
      tabs.forEach((t) => t.setAttribute('aria-selected', t.dataset.tab === key));
      panels.forEach((p) => { p.hidden = p.dataset.panel !== key; });
    };
    tabs.forEach((t) => t.addEventListener('click', () => show(t.dataset.tab)));
    if (tabs.length) show(tabs[0].dataset.tab);

    const clamp = root.querySelector('[data-clamp]');
    const more = root.querySelector('[data-readmore]');
    if (clamp && more) {
      if (clamp.scrollHeight <= clamp.clientHeight + 4) { more.hidden = true; }
      more.addEventListener('click', () => {
        const open = clamp.classList.toggle('is-open');
        more.textContent = open ? 'Read less' : 'Read more';
      });
    }
  };

  /* Article TOC: build from H2s, scroll-spy active link */
  const initToc = () => {
    const nav = document.querySelector('[data-toc-nav]');
    const body = document.querySelector('[data-article-body]');
    if (!nav || !body) return;
    const headings = [...body.querySelectorAll('h2')];
    if (headings.length < 2) { document.querySelector('[data-toc]')?.remove(); return; }

    headings.forEach((h, i) => {
      if (!h.id) h.id = 'section-' + (i + 1) + '-' + h.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
      const a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      a.dataset.tocLink = h.id;
      nav.appendChild(a);
    });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const links = nav.querySelectorAll('[data-toc-link]');
    const spy = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          links.forEach((l) => l.classList.toggle('is-active', l.dataset.tocLink === e.target.id));
        }
      });
    }, { rootMargin: '-20% 0px -70% 0px' });
    headings.forEach((h) => spy.observe(h));
  };

  /* Sticky mobile CTA: show after 400px of scroll, hide while a booking form is on screen.
     IntersectionObserver on a 1px sentinel + the [data-book-anchor] sections; no per-frame scroll work. */
  const initStickyCta = () => {
    const bar = document.querySelector('[data-sticky-cta]');
    if (!bar) return;
    const SHOW_AFTER_PX = 400;
    let pastFold = false;
    const anchorsInView = new Set();
    const update = () => { bar.hidden = !pastFold || anchorsInView.size > 0; };
    const anchors = document.querySelectorAll('[data-book-anchor]');

    if (!('IntersectionObserver' in window)) {
      /* legacy fallback only; modern browsers never reach this */
      const onScroll = () => {
        pastFold = window.scrollY > SHOW_AFTER_PX;
        anchorsInView.clear();
        anchors.forEach((a) => {
          const r = a.getBoundingClientRect();
          if (r.bottom > 0 && r.top < window.innerHeight) anchorsInView.add(a);
        });
        update();
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return;
    }

    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = `position:absolute;top:${SHOW_AFTER_PX}px;left:0;height:1px;width:1px;pointer-events:none;`;
    document.body.prepend(sentinel);
    new IntersectionObserver(([e]) => {
      pastFold = !e.isIntersecting && e.boundingClientRect.top < 0;
      update();
    }).observe(sentinel);

    if (anchors.length) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) anchorsInView.add(e.target); else anchorsInView.delete(e.target); });
        update();
      }, { threshold: 0 });
      anchors.forEach((a) => io.observe(a));
    }
  };

  /* Consult form: preselect the goal <select> from ?goal= (case-insensitive, "lose-fat" == "Lose fat") */
  const initGoalPrefill = () => {
    const selects = document.querySelectorAll('[data-goal-select]');
    if (!selects.length) return;
    const raw = new URLSearchParams(window.location.search).get('goal');
    if (!raw) return;
    const norm = (v) => v.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const want = norm(raw);
    if (!want) return;
    selects.forEach((sel) => {
      const match = [...sel.options].find((o) => norm(o.text) === want || norm(o.value) === want);
      if (match) sel.value = match.value;
    });
  };

  /* Supplement quiz: step through questions, map first answer -> result */

  /* Product recommendations: lazy-load via Shopify recommendations API */

  document.addEventListener('DOMContentLoaded', () => {
    initReveals();
    initHeader();
    initToc();
    initTabs();
    initVideoCarousels();
    initStickyCta();
    initGoalPrefill();
  });

  /* re-init reveals + product for Shopify editor section reloads */
  document.addEventListener('shopify:section:load', () => { initReveals(); initGoalPrefill(); });
})();
